import { useRef, useState } from "react";
import { Upload, Crop as CropIcon, X } from "lucide-react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";

const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const CROP_OUTPUT_SIZE = 512; // square output px after crop

/**
 * Profil-Foto Upload mit Client-seitigem Zuschneiden (1:1).
 * Wird in den Stammdaten (ProfileForm) angezeigt. Das Foto erscheint
 * im öffentlichen Vermittler-Showcase auf /team.
 */
export default function ProfilePhotoUpload({ user, onRefresh }) {
    const [photoUploading, setPhotoUploading] = useState(false);
    const [cropSrc, setCropSrc] = useState(null);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const imgRef = useRef(null);

    const initials = (user?.name || "?").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

    const onFileSelected = (file) => {
        if (!file) return;
        if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
            toast.error(`Bitte JPG, PNG oder WebP wählen (erhalten: ${file.type || "unbekannt"}).`);
            return;
        }
        if (file.size > MAX_PHOTO_BYTES) {
            const mb = (file.size / 1024 / 1024).toFixed(1);
            toast.error(`Datei ist ${mb} MB groß — Maximum 5 MB. Bitte vor dem Upload verkleinern.`);
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            setCropSrc(e.target?.result || null);
            setCrop(undefined);
            setCompletedCrop(null);
        };
        reader.readAsDataURL(file);
    };

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const initial = centerCrop(
            makeAspectCrop({ unit: "%", width: 80 }, 1, width, height),
            width,
            height,
        );
        setCrop(initial);
        setCompletedCrop(initial);
    };

    const confirmCropAndUpload = async () => {
        const image = imgRef.current;
        if (!image || !completedCrop || completedCrop.width === 0) {
            toast.error("Bitte zuerst einen Bildausschnitt wählen.");
            return;
        }
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const sx = completedCrop.x * scaleX;
        const sy = completedCrop.y * scaleY;
        const sw = completedCrop.width * scaleX;
        const sh = completedCrop.height * scaleY;
        const canvas = document.createElement("canvas");
        canvas.width = CROP_OUTPUT_SIZE;
        canvas.height = CROP_OUTPUT_SIZE;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(image, sx, sy, sw, sh, 0, 0, CROP_OUTPUT_SIZE, CROP_OUTPUT_SIZE);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
        if (!blob) {
            toast.error("Bildausschnitt konnte nicht erzeugt werden.");
            return;
        }
        setPhotoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, "vermittler-photo.jpg");
            const r = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch("/users/me/vermittler-profile", { profile_photo_url: r.data.url });
            toast.success("Profil-Foto aktualisiert");
            setCropSrc(null);
            setCompletedCrop(null);
            onRefresh?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
        } finally {
            setPhotoUploading(false);
        }
    };

    const cancelCrop = () => {
        setCropSrc(null);
        setCrop(undefined);
        setCompletedCrop(null);
    };

    const removePhoto = async () => {
        if (!window.confirm("Profil-Foto wirklich entfernen?")) return;
        setPhotoUploading(true);
        try {
            await api.patch("/users/me/vermittler-profile", { profile_photo_url: "" });
            toast.success("Profil-Foto entfernt");
            onRefresh?.();
        } catch (e) {
            toast.error("Entfernen fehlgeschlagen");
        } finally {
            setPhotoUploading(false);
        }
    };

    return (
        <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100" data-testid="vermittler-photo-row">
            <div className="w-16 h-16 rounded-sm bg-[#111827] text-[#FFCC00] flex items-center justify-center font-display font-black text-xl shrink-0 overflow-hidden">
                {user?.profile_photo_url ? (
                    <img src={user.profile_photo_url} alt="Profil-Foto" className="w-full h-full object-cover" data-testid="vermittler-photo-img" />
                ) : (
                    initials
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#111827]">Profil-Foto</div>
                <div className="text-[11px] text-gray-500">
                    Wird im öffentlichen Showcase auf <code>/team</code> angezeigt. Max. <strong>5 MB</strong>, JPG/PNG/WebP. Vor dem Upload zuschneidbar.
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <label className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm border border-gray-300 text-xs font-bold uppercase tracking-wider cursor-pointer hover:bg-gray-50 ${photoUploading ? "opacity-50 cursor-wait" : ""}`} data-testid="vermittler-photo-upload-label">
                        <Upload className="w-3 h-3" /> {photoUploading ? "Lade…" : "Hochladen"}
                        <input
                            type="file"
                            accept={ACCEPTED_PHOTO_TYPES.join(",")}
                            className="hidden"
                            disabled={photoUploading}
                            onChange={(e) => {
                                onFileSelected(e.target.files?.[0]);
                                e.target.value = "";
                            }}
                            data-testid="vermittler-photo-upload-input"
                        />
                    </label>
                    {user?.profile_photo_url && (
                        <button
                            onClick={removePhoto}
                            disabled={photoUploading}
                            className="text-xs font-bold uppercase tracking-wider text-red-700 hover:text-red-900 px-2 py-1"
                            data-testid="vermittler-photo-remove"
                        >
                            Entfernen
                        </button>
                    )}
                </div>
            </div>

            {/* Crop-Dialog */}
            {cropSrc && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    data-testid="vermittler-photo-crop-dialog"
                >
                    <div className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                            <div className="flex items-center gap-2">
                                <CropIcon className="w-4 h-4 text-[#FFCC00]" />
                                <h3 className="font-display font-bold text-sm uppercase tracking-wider">Bildausschnitt wählen</h3>
                            </div>
                            <button
                                onClick={cancelCrop}
                                disabled={photoUploading}
                                className="text-gray-500 hover:text-[#111827]"
                                aria-label="Schließen"
                                data-testid="vermittler-photo-crop-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-4">
                            <p className="text-xs text-gray-600 mb-3">
                                Verschieben &amp; skalieren Sie den Rahmen, um den quadratischen Profil-Foto-Ausschnitt zu wählen. Der gespeicherte Ausschnitt ist <strong>{CROP_OUTPUT_SIZE}×{CROP_OUTPUT_SIZE}px</strong>.
                            </p>
                            <div className="flex justify-center bg-gray-100 rounded-sm p-2 max-h-[60vh] overflow-auto">
                                <ReactCrop
                                    crop={crop}
                                    onChange={(c) => setCrop(c)}
                                    onComplete={(c) => setCompletedCrop(c)}
                                    aspect={1}
                                    keepSelection
                                    minWidth={40}
                                    circularCrop={false}
                                >
                                    <img
                                        ref={imgRef}
                                        src={cropSrc}
                                        alt="Profil-Foto Vorschau"
                                        onLoad={onImageLoad}
                                        style={{ maxHeight: "55vh", maxWidth: "100%" }}
                                        data-testid="vermittler-photo-crop-img"
                                    />
                                </ReactCrop>
                            </div>
                        </div>
                        <div className="border-t border-gray-200 px-4 py-3 flex flex-col sm:flex-row gap-2 justify-end">
                            <Button
                                variant="outline"
                                onClick={cancelCrop}
                                disabled={photoUploading}
                                className="rounded-sm font-bold uppercase tracking-wider"
                                data-testid="vermittler-photo-crop-cancel"
                            >
                                Abbrechen
                            </Button>
                            <Button
                                onClick={confirmCropAndUpload}
                                disabled={photoUploading || !completedCrop || completedCrop.width === 0}
                                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider"
                                data-testid="vermittler-photo-crop-confirm"
                            >
                                {photoUploading ? "Lade hoch…" : "Zuschneiden & Hochladen"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
